/** Read-only source selection. Email/document content is display data, never instructions. */
import { useEffect, useRef, useState } from "react";
import axios from "axios";
const ROOT = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
const client = axios.create({ withCredentials: true, timeout: 30000 });
const MAIL = `${ROOT}/api/mail-connections/icloud`;
const UC = `${ROOT}/api/service-controls/universal_credit/setup/payroll-review`;
const HASH = /^[a-f0-9]{64}$/;
const FIELD_LABELS = { total_deductions_pence: "Total deductions", gross_pay_pence: "Gross pay", net_pay_pence: "Net pay", income_tax_pence: "Income tax",
  employee_ni_pence: "Employee National Insurance", employee_pension_pence: "Employee pension", payment_date: "Pay date" };
const REVIEW_LABELS = {
  document_is_authentic_payslip: "Check that this is a genuine payslip",
  employee_and_company_identity: "Check the employee and company names",
  assessment_period_and_actual_payment_date: "Confirm the assessment period and actual payment date",
  current_period_not_year_to_date: "Check these figures are for this payday, not the year to date",
  gross_net_and_all_deductions: "Check gross pay, net pay and every deduction",
  uc_employed_earnings_evidence: "Check the employed earnings shown in Universal Credit",
  company_income_treatment: "Confirm how Universal Credit treats this company's income",
  payment_recipient_and_purpose_unverified: "Confirm who received the payment and that it was salary",
  no_exact_payment_match: "No company payment matches both the proposed net pay and pay date",
  multiple_payment_candidates: "More than one payment matches; choose the correct payment during review",
  duplicate_bank_evidence: "The bank records contain a duplicate that needs resolving",
  existing_expense_group_needs_payroll_reconciliation: "This payment is already linked to an expense; reconcile it before using it as payroll",
  payroll_payment_fields_incomplete: "Confirm net pay and the pay date before matching payments",
  pay_date_outside_assessment_period: "The proposed pay date falls outside this assessment period",
  non_positive_net_pay_needs_review: "Zero or negative net pay needs a separate review",
  password_protected_document: "This PDF is password protected and needs manual review",
  image_only_document_needs_review: "This PDF contains no readable text and needs manual review",
  document_format_needs_manual_review: "This document format needs manual review",
  document_text_unavailable: "The document text could not be read reliably",
  document_reader_busy: "The document reader is busy; try again shortly",
};
const reviewLabel = issue => {
  if (REVIEW_LABELS[issue]) return REVIEW_LABELS[issue];
  for (const [field, label] of Object.entries(FIELD_LABELS)) {
    if (issue === `${field}_needs_review`) return `${label} could not be read reliably`;
    if (issue === `${field}_ambiguous`) return `More than one ${label.toLowerCase()} figure or date was found`;
    if (issue === `${field}_out_of_range`) return `${label} is outside the supported range and needs review`;
  }
  return "An additional evidence check is required";
};
const money = value => new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(value / 100);

export default function UCPayrollPreview({ setupRevision, sourceSnapshot, autoSearch = false }) {
  const [query, setQuery] = useState("payslip");
  const [searchedQuery, setSearchedQuery] = useState("");
  const [search, setSearch] = useState(null);
  const [selected, setSelected] = useState(null);
  const [files, setFiles] = useState(null);
  const [preview, setPreview] = useState(null);
  const [reviewFile, setReviewFile] = useState(null);
  const [decision, setDecision] = useState("needs_evidence");
  const [reason, setReason] = useState("");
  const [payment, setPayment] = useState("");
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const epoch = useRef(0), locked = useRef(false);
  useEffect(() => {
    epoch.current += 1;
    setSearch(null); setSelected(null); setFiles(null); setPreview(null); setError(""); setBusy(false); locked.current = false;
    return () => { epoch.current += 1; };
  }, [setupRevision, sourceSnapshot]);

  const run = async (action, failure) => {
    if (locked.current) return;
    locked.current = true;
    const version = epoch.current;
    setBusy(true); setError(""); setSaved(false);
    try {
      const apply = await action();
      if (version === epoch.current) apply();
    } catch (_) {
      if (version === epoch.current) { setPreview(null); setError(failure); }
    } finally {
      if (version === epoch.current) { locked.current = false; setBusy(false); }
    }
  };

  const find = (cursor = null) => {
    if (!query.trim() || busy) return;
    const term = cursor ? searchedQuery : query.trim();
    setSearch(null); setSelected(null); setFiles(null); setPreview(null);
    run(async () => {
      const { data } = await client.post(`${MAIL}/search`, { query: term, limit: 10,
        mailbox_scope: "all", sender_scope: "all", cursor });
      if (!Array.isArray(data?.results) || !data.results.every(row => typeof row.id === "string"
          && typeof row.subject === "string" && typeof row.from === "string" && typeof row.date === "string")
          || typeof data.has_more !== "boolean" || (data.has_more && typeof data.next_cursor !== "string")
          || !Array.isArray(data.coverage?.failed_folders)) throw new Error("Invalid search");
      return () => { setSearch(data); setSearchedQuery(term); };
    }, "Email search could not be completed. Check your email connection and search again.");
  };

  useEffect(() => {
    if (!autoSearch) return undefined;
    let cancelled = false;
    // Defer until the committed mount; StrictMode's discarded mount never searches.
    Promise.resolve().then(() => { if (!cancelled) find(); });
    return () => { cancelled = true; };
    // Search once per source-bound handoff, not after editing the query/results.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoSearch, setupRevision, sourceSnapshot]);

  const chooseMessage = message => {
    if (locked.current) return;
    setSelected(null); setFiles(null); setPreview(null);
    run(async () => {
      const { data } = await client.get(`${MAIL}/messages/${encodeURIComponent(message.id)}/attachments`);
      if (data?.message_id !== message.id || !Array.isArray(data.attachments)
          || !data.attachments.every(file => typeof file.filename === "string" && typeof file.retrievable === "boolean"
            && (!file.retrievable || typeof file.retrieval_token === "string"))) throw new Error("Invalid attachments");
      return () => { setSelected(message); setFiles(data.attachments); };
    }, "Attachments could not be checked. Search again if the message or connection changed.");
  };

  const check = file => {
    if (locked.current || !selected) return;
    const message = selected;
    setPreview(null);
    run(async () => {
      const { data } = await client.post(UC, { message_id: message.id, retrieval_token: file.retrieval_token,
        setup_revision: setupRevision, source_snapshot_sha256: sourceSnapshot },
      { headers: { "x-ndg-service-control": "browser-v1" } });
      if (data?.state !== "payroll_review_required" || data.ready_to_submit !== false || data.active !== false
          || data.setup_revision !== setupRevision || data.source_snapshot_sha256 !== sourceSnapshot
          || data.source_message_id !== message.id || data.document_sha256 !== file.sha256 || !HASH.test(data.document_sha256)
          || !data.fields || !Array.isArray(data.extraction_issues) || !Array.isArray(data.required_reviews)
          || !Array.isArray(data.payment_comparison?.candidates) || !Array.isArray(data.payment_comparison?.issues)
          || typeof data.notice !== "string" || !HASH.test(data.review_binding_sha256)
          || !Number.isSafeInteger(data.owner_review?.revision)) throw new Error("Invalid payroll preview");
      for (const [key, field] of Object.entries(data.fields)) {
        if (!Object.hasOwn(FIELD_LABELS, key) || !Number.isInteger(field.line)
            || (key === "payment_date" ? typeof field.value !== "string" : !Number.isSafeInteger(field.value))) {
          throw new Error("Invalid proposed field");
        }
      }
      return () => {
        setPreview(data); setReviewFile(file);
        const review = data.owner_review.latest?.review;
        setDecision(review?.decision || "needs_evidence"); setReason(review?.reason || ""); setPayment(review?.payment_reference || "");
      };
    }, "Payroll check was not completed. Reload evidence and select the attachment again; the source or access may have changed.");
  };

  const saveReview = event => {
    event.preventDefault();
    if (locked.current || !preview || !reviewFile || !reason.trim()) return;
    const current = preview;
    run(async () => {
      const { data } = await client.put(UC, {
        message_id: selected.id, retrieval_token: reviewFile.retrieval_token,
        setup_revision: setupRevision, source_snapshot_sha256: sourceSnapshot,
        request_id: crypto.randomUUID(), expected_review_revision: current.owner_review.revision,
        review_binding_sha256: current.review_binding_sha256,
        review: { decision, reason: reason.trim(), payment_reference: decision === "not_payroll" ? null : payment || null },
      }, { headers: { "x-ndg-service-control": "browser-v1" } });
      if (data.review_binding_sha256 !== current.review_binding_sha256 || data.active !== false || data.ready_to_submit !== false
          || data.owner_review?.revision !== current.owner_review.revision + 1) throw new Error("Invalid save");
      return () => { setPreview(data); setSaved(true); };
    }, "The review could not be confirmed as saved. Check the payslip again to reload its saved review before retrying.");
  };

  const control = "min-h-11 px-4 py-2 border rounded";
  return <section className="space-y-3 border-t pt-3" data-testid="uc-payroll-preview">
    <p className="font-semibold">Check an emailed payslip</p>
    <p className="text-sm">Choose an email attachment to compare with company payments. You can save your review notes and proposed match. Verification is still required before figures can be used.</p>
    <form className="space-y-2" onSubmit={event => { event.preventDefault(); find(); }}>
      <label htmlFor="uc-payroll-search" className="block">Find emails about</label>
      <input id="uc-payroll-search" value={query} maxLength={200} disabled={busy} onChange={event => setQuery(event.target.value)}
        className="block w-full max-w-full p-3 border rounded bg-white text-slate-900 dark:bg-slate-800 dark:text-white" />
      <button type="submit" disabled={busy || !query.trim()} className={control}>Search connected email</button>
    </form>
    {busy && <p role="status">Checking the selected evidence…</p>}
    {error && <p role="alert">{error}</p>}
    {search && <>
      <p className="text-sm">This searches messages across accessible folders. It does not search inside every attachment.</p>
      {search.coverage.failed_folders.length > 0 && <p role="alert">Some folders could not be searched. The results are incomplete.</p>}
      {!search.results.length && <p>No matching messages on this page.</p>}
      <ul className="space-y-2">{search.results.map(message => <li key={message.id} className="break-words">
        <p>{message.subject || "No subject"}</p><p className="text-sm">{message.from} · {message.date}</p>
        <button type="button" disabled={busy} onClick={() => chooseMessage(message)} className={control}>Choose this email</button>
      </li>)}</ul>
      {search.has_more && <button type="button" disabled={busy} onClick={() => find(search.next_cursor)} className={control}>Next results</button>}
    </>}
    {selected && files && <div className="space-y-2">
      <p className="font-semibold break-words">Attachments: {selected.subject}</p>
      {!files.length && <p>No attached documents were found. A link to a payroll portal needs separate access.</p>}
      {files.map((file, index) => <div key={index} className="space-y-1 break-words">
        <p>{file.filename}</p>
        <button type="button" disabled={busy || !file.retrievable} onClick={() => check(file)} className={control}>Check this payslip</button>
        {!file.retrievable && <p className="text-sm">This file type or size needs manual review.</p>}
      </div>)}
    </div>}
    {preview && <div className="space-y-2" data-testid="uc-payroll-result">
      <p role="status">{preview.notice}</p>
      <dl>{Object.entries(preview.fields).map(([key, field]) => <div key={key}>
        <dt className="font-semibold">{FIELD_LABELS[key]} — proposed</dt><dd>{key === "payment_date" ? field.value : money(field.value)}</dd>
      </div>)}</dl>
      {!Object.keys(preview.fields).length && <p>No reliable labelled figures could be extracted. This payslip needs manual review.</p>}
      {preview.payslip_consistency && <p>{preview.payslip_consistency.state === "consistent"
        ? "Gross pay minus total deductions agrees with net pay. This checks the arithmetic only."
        : preview.payslip_consistency.state === "mismatch" ? "The payslip totals do not balance. Check the original document before proceeding."
        : "The payslip does not provide all three labelled totals needed to check its arithmetic."}</p>}
      {preview.reconciliation?.state === "proposed_existing_expense_link" && <p>This proposed match links to the existing expense. It does not create a second salary deduction.</p>}
      <p>{preview.payment_comparison.candidates.length} possible payment match(es). A matching amount and date do not verify the recipient or payroll.</p>
      <ul className="list-disc pl-5">{[...preview.extraction_issues, ...preview.payment_comparison.issues].map((issue, index) =>
        <li key={index}>{reviewLabel(issue)}</li>)}</ul>
      <form onSubmit={saveReview} className="space-y-2">
        <label htmlFor="uc-payroll-decision" className="block">Your assessment</label>
        <select id="uc-payroll-decision" className="block w-full p-3 border rounded" value={decision} disabled={busy} onChange={e => setDecision(e.target.value)}>
          <option value="needs_evidence">More evidence needed</option>
          <option value="reviewed">I have reviewed this payslip</option>
          <option value="not_payroll">This is not payroll evidence</option>
        </select>
        {decision !== "not_payroll" && <>
          <label htmlFor="uc-payroll-payment" className="block">Proposed company payment</label>
          <select id="uc-payroll-payment" className="block w-full p-3 border rounded" value={payment} disabled={busy} onChange={e => setPayment(e.target.value)}>
            <option value="">No payment selected</option>
            {preview.payment_comparison.candidates.map((item, index) => <option key={item.reference} value={item.reference}>
              Payment {index + 1}{item.already_grouped_with_expense ? " — already linked to an expense" : ""}
            </option>)}
          </select>
        </>}
        <label htmlFor="uc-payroll-reason" className="block">What did you check, or what is missing?</label>
        <textarea id="uc-payroll-reason" className="block w-full p-3 border rounded" required maxLength={1000} value={reason} disabled={busy} onChange={e => setReason(e.target.value)} />
        <button type="submit" disabled={busy || !reason.trim()} className={control}>Save payroll review</button>
        {preview.owner_review.revision > 0 && <p>Saved review version {preview.owner_review.revision}. Verification remains required.</p>}
        {saved && <p role="status">Payroll review saved. No earnings have been approved.</p>}
      </form>
      <p className="font-semibold">Still requires verification</p>
      <ul className="list-disc pl-5">{preview.required_reviews.map((issue, index) => <li key={index}>{reviewLabel(issue)}</li>)}</ul>
    </div>}
  </section>;
}
